import { useState, useCallback } from 'react';
import { fetchGraphql } from '../graphql/fetch';

export type SelectionStatus = 'idle' | 'saving' | 'success' | 'error';

export interface SelectionContext {
  selectedCount: number;
  remaining: number;
  selectedSubmissionIds: string[];
}

// One class, code-discriminated — the useRegistration convention. Codes:
// SUBMISSION_NOT_FOUND | SELECTION_CLOSED | NOT_YOUR_SUBMISSION |
// SUBMISSION_NOT_ELIGIBLE | SELECTION_LIMIT_REACHED | GRAPHQL | UNKNOWN.
export class SelectionError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'SelectionError';
  }
}

interface MutationPayload {
  submission: { id: string } | null;
  selectionContext: SelectionContext | null;
  errors: Array<{ code: string | null; message: string }>;
}

const SELECT_MUTATION = `
  mutation SelectSubmission($submissionId: ID!) {
    selectSubmission(input: { submissionId: $submissionId }) {
      submission { id }
      selectionContext { selectedCount remaining selectedSubmissionIds }
      errors { code message }
    }
  }
`;

const DESELECT_MUTATION = `
  mutation DeselectSubmission($submissionId: ID!) {
    deselectSubmission(input: { submissionId: $submissionId }) {
      submission { id }
      selectionContext { selectedCount remaining selectedSubmissionIds }
      errors { code message }
    }
  }
`;

// Team-level submission selection for post-round evaluation. The returned
// `context` is the mutation payload's selectionContext — authoritative
// post-mutation state (SSR props are cached and may be stale; never reload
// to refresh selection state).
export function useSelection() {
  const [status, setStatus]     = useState<SelectionStatus>('idle');
  const [error, setError]       = useState<SelectionError | null>(null);
  const [context, setContext]   = useState<SelectionContext | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const run = useCallback(async (mutation: string, field: 'selectSubmission' | 'deselectSubmission', submissionId: string): Promise<SelectionContext> => {
    setStatus('saving');
    setError(null);
    setPendingId(submissionId);
    try {
      const res = await fetchGraphql<Record<string, MutationPayload>>({
        query: mutation,
        variables: { submissionId },
      });

      // Top-level GraphQL transport errors (fetchGraphql returns { data, errors },
      // it does not throw) — same convention as useRegistration.
      if (res.errors && res.errors.length) {
        throw new SelectionError('GRAPHQL', res.errors[0].message);
      }

      const payload = res.data && res.data[field];
      if (!payload) throw new SelectionError('UNKNOWN', 'Empty response from server');
      if (payload.errors && payload.errors.length) {
        const first = payload.errors[0];
        throw new SelectionError(first.code || 'UNKNOWN', first.message);
      }
      const next = payload.selectionContext;
      if (!next) throw new SelectionError('UNKNOWN', 'Missing selection context in response');
      setContext(next);
      setStatus('success');
      setPendingId(null);
      return next;
    } catch (e) {
      const typed = e instanceof SelectionError ? e : new SelectionError('GRAPHQL', e instanceof Error ? e.message : String(e));
      setError(typed);
      setStatus('error');
      setPendingId(null);
      throw typed;
    }
  }, []);

  const select   = useCallback((submissionId: string) => run(SELECT_MUTATION, 'selectSubmission', submissionId), [run]);
  const deselect = useCallback((submissionId: string) => run(DESELECT_MUTATION, 'deselectSubmission', submissionId), [run]);

  return { select, deselect, status, error, context, pendingId };
}
